/**
 * facets — what a pack is, coarse and never shown: one mechanism for every house that has a wall.
 *
 * A house declares a short list of facets (an axis: theme, setting, era…), each a closed list of
 * values. A pack takes at most one value per facet, exactly one for a `required` facet. A tile carries
 * its pack's values as tokens `facet:value` in `images.wall_tiles.facets` (written by the shared
 * ingest from the classification artifact, `scripts/wall/facets/`), together with the tokens of the
 * facets its own cell decides (`level: 'tile'`). They are the substrate of the viewer profile (plan
 * `2026_10_29_wall_keywords_profile.md`, Phase B): what a visitor tends to like, weighed from the tiles
 * they touch. They do not scope the wall and no tile shows them; what a visitor asks for is keywords
 * (`tsQuery.ts` pills, chips taken from the prompts).
 *
 * The axes several houses share (region, era, occasion, season, setting) have one vocabulary, here;
 * an axis that is a house's own (Narcis's theme, Toile's use) is declared by that house beside its
 * theme, from these types. Ids are stable: a stored token outlives a label change, never an id change.
 *
 * Pure — no I/O — so the client, the ingest and the classifier export read the same functions.
 */

export type FacetValue = {
    id: string
    label: string
    /** What the search field shows and matches: lowercase, one or two plain words. */
    short: string
    /** A colour value's own colour as a hex, the same in every house; none for a value that is no single colour. */
    swatch?: string
    meta: string
}

export type FacetDef = {
    key: string
    label: string
    /** Exactly one value per pack. The house's first facet is required. */
    required: boolean
    /** Where the facet is decided: the pack (default; every cell of a pack carries it) or the cell
     *  (it varies inside packs, so a tile carries its own). A key lives at one level only. */
    level?: 'pack' | 'tile'
    values: readonly FacetValue[]
}

export type HouseFacets = readonly FacetDef[]

/** `facet:value`. */
export function facetToken(key: string, value: string): string {
    return `${key}:${value}`
}

/** The facets a cell decides for itself, in the house's declaration order. */
export function tileLevelFacets(facets: HouseFacets): FacetDef[] {
    return facets.filter((f) => f.level === 'tile')
}

/**
 * One classification (`{facet: value}`, as the artifact stores it) checked against the facets of one
 * level (`'pack'`, a pack's own; `'tile'`, a cell's own) and turned into tokens. Returns the problems
 * instead of tokens when a value is unknown, a facet belongs to the other level, or a required facet
 * is missing, so the ingest can name them.
 */
export function levelFacetTokens(
    assigned: Record<string, string>,
    allFacets: HouseFacets,
    level: 'pack' | 'tile',
): {ok: true; tokens: string[]} | {ok: false; problems: string[]} {
    const facets = allFacets.filter((f) => (f.level ?? 'pack') === level)
    const problems: string[] = []
    const tokens: string[] = []
    for (const def of facets) {
        const value = assigned[def.key]
        if (value === undefined) {
            if (def.required) problems.push(`missing required facet ${def.key}`)
            continue
        }
        if (!def.values.some((v) => v.id === value)) {
            problems.push(`unknown value ${def.key}:${value}`)
            continue
        }
        tokens.push(facetToken(def.key, value))
    }
    for (const key of Object.keys(assigned)) {
        if (!facets.some((f) => f.key === key)) problems.push(`unknown facet ${key}`)
    }
    return problems.length ? {ok: false, problems} : {ok: true, tokens: tokens.sort()}
}

export function packFacetTokens(assigned: Record<string, string>, facets: HouseFacets) {
    return levelFacetTokens(assigned, facets, 'pack')
}

// ─── Shared vocabularies ───────────────────────────────────────────────────────────────────────

export const REGION: FacetDef = {
    key: 'region',
    label: 'Region',
    required: false,
    values: [
        {id: 'east-asia', label: 'East Asia', short: 'east asia', meta: 'China, Japan, Korea, Mongolia, Taiwan'},
        {id: 'south-asia', label: 'South Asia', short: 'south asia', meta: 'India, Pakistan, Bangladesh, Sri Lanka, Nepal'},
        {id: 'southeast-asia', label: 'Southeast Asia', short: 'southeast asia', meta: 'Vietnam, Thailand, Indonesia, the Philippines, Malaysia'},
        {id: 'middle-east', label: 'Middle East & North Africa', short: 'middle east', meta: 'Arab world, Iran, Turkey, the Maghreb, Central Asia'},
        {id: 'africa', label: 'Sub-Saharan Africa', short: 'africa', meta: 'West, East, Central and Southern Africa'},
        {id: 'europe', label: 'Europe', short: 'europe', meta: 'Europe, the UK, Russia, the Caucasus'},
        {id: 'north-america', label: 'North America', short: 'north america', meta: 'United States, Canada'},
        {id: 'latin-america', label: 'Latin America & Caribbean', short: 'latin america', meta: 'Mexico, Central and South America, the Caribbean'},
        {id: 'oceania', label: 'Oceania & Pacific', short: 'oceania', meta: 'Australia, New Zealand, the Pacific islands'},
    ],
}

export const ERA: FacetDef = {
    key: 'era',
    label: 'Era',
    required: false,
    values: [
        {id: 'ancient', label: 'Antiquity', short: 'ancient', meta: 'Before 500: Egypt, Greece, Rome, ancient Asia and the Americas'},
        {id: 'medieval', label: 'Middle Ages', short: 'medieval', meta: '500 to 1500: knights, Vikings, caliphates, dynasties'},
        {id: 'early-modern', label: '1500 to 1800', short: 'early modern', meta: 'Renaissance, baroque, Tudors, Mughals, Edo'},
        {id: 'nineteenth', label: '1800s', short: '1800s', meta: 'Regency, Victorian, the frontier, the Second Empire'},
        {id: 'early-1900s', label: '1900 to 1959', short: 'early 1900s', meta: 'Belle Époque, the twenties, the war years, the fifties'},
        {id: 'late-1900s', label: '1960 to 1999', short: 'late 1900s', meta: 'Sixties to nineties'},
        {id: 'contemporary', label: 'Today', short: 'today', meta: 'The present day'},
        {id: 'timeless', label: 'Timeless & imagined', short: 'timeless', meta: 'Myth, fantasy, science fiction, no datable period'},
    ],
}

export const OCCASION: FacetDef = {
    key: 'occasion',
    label: 'Occasion',
    required: false,
    values: [
        {id: 'wedding', label: 'Weddings', short: 'wedding', meta: 'Bride, groom, ceremonies, bridal parties, wedding guests'},
        {id: 'christmas-new-year', label: 'Christmas & New Year', short: 'christmas', meta: 'Christmas, Hanukkah, New Year, the winter holidays'},
        {id: 'halloween', label: 'Halloween', short: 'halloween', meta: 'Halloween, Day of the Dead'},
        {id: 'valentines', label: "Valentine's", short: "valentine's", meta: "Valentine's Day, anniversaries, love notes"},
        {id: 'festival', label: 'Festivals & holy days', short: 'festival', meta: 'Diwali, Eid, Holi, Lunar New Year, Easter, Nowruz, carnival'},
        {id: 'birthday-party', label: 'Birthdays & parties', short: 'birthday', meta: 'Birthdays, showers, bachelorettes, parties'},
        {id: 'graduation', label: 'School & graduation', short: 'graduation', meta: 'Back to school, prom, graduation, teachers'},
        {id: 'family', label: 'Baby & family', short: 'family', meta: "Baby announcements, Mother's and Father's Day, family days"},
        {id: 'national-day', label: 'National days', short: 'national day', meta: 'Independence and Republic days, national and civic holidays'},
        {id: 'game-day', label: 'Game day', short: 'game day', meta: 'Matches, tournaments, supporters'},
        {id: 'gala', label: 'Galas & red carpets', short: 'gala', meta: 'Premieres, film festivals, balls, award nights'},
        {id: 'harvest', label: 'Harvest & Thanksgiving', short: 'harvest', meta: 'Thanksgiving, harvest festivals'},
    ],
}

export const SEASON: FacetDef = {
    key: 'season',
    label: 'Season',
    required: false,
    values: [
        {id: 'spring', label: 'Spring', short: 'spring', meta: 'Blossom, Easter, April and May'},
        {id: 'summer', label: 'Summer', short: 'summer', meta: 'Heat, beach, holidays'},
        {id: 'autumn', label: 'Autumn', short: 'autumn', meta: 'Leaves, harvest, Halloween'},
        {id: 'winter', label: 'Winter', short: 'winter', meta: 'Snow, Christmas, cold'},
    ],
}

export const SETTING: FacetDef = {
    key: 'setting',
    label: 'Setting',
    required: false,
    values: [
        {id: 'studio', label: 'Studio', short: 'studio', meta: 'Plain backdrop, studio light'},
        {id: 'interior', label: 'Indoors', short: 'indoors', meta: 'Homes, offices, bedrooms, palaces seen from inside'},
        {id: 'city', label: 'City streets', short: 'city', meta: 'Streets, rooftops, cafés, transit'},
        {id: 'landmark', label: 'Landmarks', short: 'landmark', meta: 'A named, recognisable monument or place'},
        {id: 'nature', label: 'Nature', short: 'nature', meta: 'Fields, forests, mountains, gardens, deserts'},
        {id: 'water', label: 'Beach & water', short: 'water', meta: 'Beaches, pools, lakes, boats, the sea'},
        {id: 'venue', label: 'Venues & stages', short: 'venue', meta: 'Stadiums, ballrooms, clubs, stages, red carpets'},
        {id: 'imagined', label: 'Imagined worlds', short: 'imagined', meta: 'Fantasy, space, the otherworld'},
    ],
}

export const COLOUR: FacetDef = {
    key: 'colour',
    label: 'Colour',
    required: false,
    values: [
        {id: 'black', label: 'Black', short: 'black', swatch: '#111111', meta: 'Black, charcoal, noir, dark'},
        {id: 'white', label: 'White', short: 'white', swatch: '#F5F5F0', meta: 'White, ivory, cream, pale'},
        {id: 'grey', label: 'Grey', short: 'grey', swatch: '#8A8A8A', meta: 'Grey, silver, steel, monochrome grey'},
        {id: 'red', label: 'Red', short: 'red', swatch: '#D2312E', meta: 'Red, scarlet, crimson, burgundy'},
        {id: 'orange', label: 'Orange', short: 'orange', swatch: '#EE8228', meta: 'Orange, amber, terracotta, rust'},
        {id: 'yellow', label: 'Yellow', short: 'yellow', swatch: '#F2C830', meta: 'Yellow, mustard, lemon'},
        {id: 'gold', label: 'Gold', short: 'gold', swatch: '#C9A227', meta: 'Gold, metallic, brass, bronze'},
        {id: 'green', label: 'Green', short: 'green', swatch: '#3E9B4F', meta: 'Green, olive, emerald, sage'},
        {id: 'blue', label: 'Blue', short: 'blue', swatch: '#2F6FD0', meta: 'Blue, navy, teal, turquoise, indigo'},
        {id: 'purple', label: 'Purple', short: 'purple', swatch: '#7B4FB5', meta: 'Purple, violet, lavender, plum'},
        {id: 'pink', label: 'Pink', short: 'pink', swatch: '#EC7FA9', meta: 'Pink, rose, magenta, blush'},
        {id: 'brown', label: 'Brown', short: 'brown', swatch: '#7A5230', meta: 'Brown, tan, beige, sepia, earth tones'},
        {id: 'multicolour', label: 'Many colours', short: 'multicolour', meta: 'Rainbow, bright multicolour, a full palette'},
    ],
}
